import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/auth/AuthContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}


const MEDIA_STYLE = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

function MediaSlide({ item, opacity, isCurrent, onError, onEnded }) {
  if (!item) return null;
  const isVideo = item.resource_type === "video";
  const style = { ...MEDIA_STYLE, opacity, transition: `opacity ${1000}ms ease-in-out` };
  if (isVideo) {
    return (
      <video
        key={item.public_url + (isCurrent ? "-current" : "-next")}
        src={item.public_url}
        autoPlay
        muted
        playsInline
        preload="auto"
        style={style}
        onError={onError}
        onEnded={onEnded}
      />
    );
  }
  return (
    <img
      key={item.public_url + (isCurrent ? "-current" : "-next")}
      src={item.public_url}
      alt=""
      style={style}
      draggable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SLIDE_DURATION_MS = 8000;
const CROSSFADE_MS = 1000;
const POLL_INTERVAL_MS = 15000;
const CURSOR_HIDE_DELAY_MS = 3000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LiveSlideshow() {
  const { slug } = useParams();

  // -- wedding metadata --
  const [wedding, setWedding] = useState(null);

  // -- loading / error states --
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [errorType, setErrorType] = useState(null); // "403" | "404"

  // -- photo state --
  const [allPhotos, setAllPhotos] = useState([]); // every photo fetched so far
  const queueRef = useRef([]); // upcoming photos (mutable, no re-render needed)
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [nextPhoto, setNextPhoto] = useState(null);
  const [showNext, setShowNext] = useState(false); // drives crossfade opacity

  // Track the created_at of the most recent fetch for polling
  const lastFetchedAtRef = useRef(null);
  const allPhotosRef = useRef([]); // mirror of allPhotos for use inside callbacks
  // Tracks the public_url of the last shown item to prevent consecutive repeats
  const lastShownRef = useRef(null);

  // -- cursor hide --
  const [cursorVisible, setCursorVisible] = useState(true);
  const cursorTimerRef = useRef(null);

  // -- fullscreen triggered on first click --
  const fullscreenTriggeredRef = useRef(false);

  // -- slideshow loop refs --
  const slideshowTimerRef = useRef(null);
  const crossfadeTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const isRunningRef = useRef(false);
  const mountedRef = useRef(true);

  // ---------------------------------------------------------------------------
  // Cursor hide logic
  // ---------------------------------------------------------------------------

  const resetCursorTimer = useCallback(() => {
    setCursorVisible(true);
    if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    cursorTimerRef.current = setTimeout(() => setCursorVisible(false), CURSOR_HIDE_DELAY_MS);
  }, []);

  useEffect(() => {
    resetCursorTimer();
    return () => {
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, [resetCursorTimer]);

  // ---------------------------------------------------------------------------
  // Fullscreen on first click
  // ---------------------------------------------------------------------------

  const handlePageClick = useCallback(() => {
    if (!fullscreenTriggeredRef.current) {
      fullscreenTriggeredRef.current = true;
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Slideshow loop
  // ---------------------------------------------------------------------------

  const advanceSlide = useCallback(() => {
    // Clear any pending timers to avoid double-advance (e.g. onError + timer)
    clearTimeout(slideshowTimerRef.current);
    clearTimeout(crossfadeTimerRef.current);

    // If queue is empty, re-shuffle allPhotos into it
    if (queueRef.current.length === 0) {
      const all = allPhotosRef.current;
      if (all.length === 0) return; // nothing to show yet
      const shuffled = shuffle(all);
      // Prevent the first item of the new loop from being the same as the last shown
      if (shuffled.length > 1 && shuffled[0].public_url === lastShownRef.current) {
        const swap = Math.floor(Math.random() * (shuffled.length - 1)) + 1;
        [shuffled[0], shuffled[swap]] = [shuffled[swap], shuffled[0]];
      }
      queueRef.current = shuffled;
    }

    const photo = queueRef.current.shift();
    lastShownRef.current = photo.public_url;

    if (photo.resource_type === "video") {
      // Hard cut for videos to avoid DOM remount. No timer — onEnded advances.
      setNextPhoto(null);
      setShowNext(false);
      setCurrentPhoto(photo);
      return;
    }

    setNextPhoto(photo);
    setShowNext(true);

    // After crossfade completes, swap current ← next
    crossfadeTimerRef.current = setTimeout(() => {
      setCurrentPhoto(photo);
      setNextPhoto(null);
      setShowNext(false);
    }, CROSSFADE_MS);

    // Schedule next advance
    slideshowTimerRef.current = setTimeout(advanceSlide, SLIDE_DURATION_MS);
  }, []);

  const startSlideshow = useCallback(
    (photos) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;

      // Shuffle and load queue
      queueRef.current = shuffle(photos);

      // Show first photo immediately (no crossfade from black)
      const first = queueRef.current.shift();
      lastShownRef.current = first.public_url;
      setCurrentPhoto(first);

      // Videos advance via onEnded; images use a timer
      if (first.resource_type !== "video") {
        slideshowTimerRef.current = setTimeout(advanceSlide, SLIDE_DURATION_MS);
      }
    },
    [advanceSlide]
  );

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------

  const poll = useCallback(async () => {
    try {
      const body = { slug };
      if (lastFetchedAtRef.current) body.after = lastFetchedAtRef.current;

      const { data, error } = await supabase.functions.invoke("live-photos", { body });

      if (!mountedRef.current) return;

      if (error) return; // silently continue

      const photos = data?.photos ?? [];
      if (photos.length === 0) return;

      // Update lastFetchedAt to the most recent photo in this batch
      const newestCreatedAt = photos.reduce((max, p) =>
        p.created_at > max ? p.created_at : max, photos[0].created_at
      );
      lastFetchedAtRef.current = newestCreatedAt;

      if (!mountedRef.current) return;

      setAllPhotos((prev) => {
        const updated = [...prev, ...photos];
        allPhotosRef.current = updated;
        return updated;
      });

      if (!isRunningRef.current && allPhotosRef.current.length > 0) {
        // First photos just arrived — start slideshow
        startSlideshow(allPhotosRef.current);
      } else {
        // Prepend new photos to the queue so they show soon.
        // Don't put the currently showing photo at the very front (would repeat it).
        const currentUrl = lastShownRef.current;
        const notCurrent = photos.filter((p) => p.public_url !== currentUrl);
        const isCurrent = photos.filter((p) => p.public_url === currentUrl);
        queueRef.current = [...notCurrent, ...queueRef.current, ...isCurrent];
      }
    } catch {
      // network error — silently ignore
    }
  }, [slug, startSlideshow]);

  // ---------------------------------------------------------------------------
  // Initial fetch
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoadingInitial(true);
      setErrorType(null);

      // 1) Fetch wedding metadata via RPC (bypasses public SELECT policy)
      const { data: weddingArr } = await supabase.rpc("get_wedding_by_slug", { p_slug: slug });

      if (cancelled) return;

      const weddingData = weddingArr?.[0] ?? null;
      if (!weddingData) {
        setErrorType("404");
        setLoadingInitial(false);
        return;
      }

      setWedding(weddingData);

      // 2) Fetch initial photos batch
      const { data, error } = await supabase.functions.invoke("live-photos", {
        body: { slug },
      });

      if (cancelled) return;

      if (error) {
        // Try to parse error details
        const msg = typeof error === "object" ? JSON.stringify(error) : String(error);
        if (msg.includes("403") || msg.includes("premium")) {
          setErrorType("403");
          setLoadingInitial(false);
          return;
        }
        if (msg.includes("404")) {
          setErrorType("404");
          setLoadingInitial(false);
          return;
        }
        // Other error: show 0-photo state
        setLoadingInitial(false);
        return;
      }

      // Check for function-level error codes in data
      if (data?.error === "premium_required") {
        setErrorType("403");
        setLoadingInitial(false);
        return;
      }

      const photos = data?.photos ?? [];

      if (photos.length > 0) {
        // Track the newest created_at for subsequent polling
        const newestCreatedAt = photos.reduce((max, p) =>
          p.created_at > max ? p.created_at : max, photos[0].created_at
        );
        lastFetchedAtRef.current = newestCreatedAt;

        allPhotosRef.current = photos;
        setAllPhotos(photos);
      }

      setLoadingInitial(false);

      if (photos.length > 0) {
        startSlideshow(photos);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [slug, startSlideshow]);

  // ---------------------------------------------------------------------------
  // Start polling after initial load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (loadingInitial) return;
    if (errorType) return;

    // Start polling
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [loadingInitial, errorType, poll]);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      isRunningRef.current = false;
      if (slideshowTimerRef.current) clearTimeout(slideshowTimerRef.current);
      if (crossfadeTimerRef.current) clearTimeout(crossfadeTimerRef.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const containerStyle = {
    position: "fixed",
    inset: 0,
    background: "#000",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: cursorVisible ? "default" : "none",
    userSelect: "none",
  };

  // -- Loading spinner --
  if (loadingInitial) {
    return (
      <div
        style={containerStyle}
        onMouseMove={resetCursorTimer}
        onClick={handlePageClick}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            border: "4px solid white",
            borderTopColor: "transparent",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // -- 403 premium required --
  if (errorType === "403") {
    return (
      <div style={containerStyle}>
        <p style={{ color: "#fff", fontSize: "1.25rem", textAlign: "center" }}>
          Funzionalità riservata al piano Premium
        </p>
      </div>
    );
  }

  // -- 404 event not found --
  if (errorType === "404") {
    return (
      <div style={containerStyle}>
        <p style={{ color: "#fff", fontSize: "1.25rem", textAlign: "center" }}>
          Evento non trovato
        </p>
      </div>
    );
  }

  // -- 0-photo state --
  if (allPhotos.length === 0) {
    return (
      <div
        style={containerStyle}
        onMouseMove={resetCursorTimer}
        onClick={handlePageClick}
      >
        <div style={{ textAlign: "center", color: "#fff", padding: "2rem" }}>
          <p style={{ fontSize: "2rem", fontWeight: 700, marginBottom: "0.5rem" }}>
            Carica la prima foto!
          </p>
          <p style={{ fontSize: "1rem", opacity: 0.7, marginBottom: "1.5rem" }}>
            Scansiona il QR code per caricare le tue foto
          </p>
          {false && (
            <img
              src=""
              alt="QR code"
              style={{
                maxWidth: 200,
                maxHeight: 200,
                background: "#fff",
                borderRadius: 12,
                display: "block",
                margin: "0 auto",
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // -- Slideshow --
  return (
    <div
      style={containerStyle}
      onMouseMove={resetCursorTimer}
      onClick={handlePageClick}
    >
      <MediaSlide
        item={currentPhoto}
        opacity={showNext ? 0 : 1}
        isCurrent={true}
        onError={advanceSlide}
        onEnded={advanceSlide}
      />
      <MediaSlide
        item={nextPhoto}
        opacity={showNext ? 1 : 0}
        isCurrent={false}
        onError={advanceSlide}
      />
    </div>
  );
}
